import { inject, Injectable } from '@angular/core';

// Services
import { FieldService } from '@portal/app/shared/services/field.service';
import { StandardDatePresetService } from '@portal/app/shared/services/standard-date-preset.service';

// Types
import type { SelectItem, SelectItemGroup } from 'primeng/api';
import { MetricsGroupOptions } from '@portal/app/shared/utils';

// Constants
const DEFAULT_METRICS = [
  'mediaSpend',
  'totalSales',
  'totalOrders',
  'salesI',
  'salesLT',
  'cpoI',
  'cpoLT',
  'roasI',
  'roasLT',
  'percSalesI',
  'percOrdersI',
  'ordersI'
];

@Injectable({
  providedIn: 'root'
})
export class DropdownService {
  private standardPresetService = inject(StandardDatePresetService);

  constructor(private fieldService: FieldService) {}

  getRelativeDayOptions(): SelectItem[] {
    const options = this.standardPresetService.currentRangeOptions;

    return options.map((option) => ({
      label: option.label || option.name,
      value: option.name
    }));
  }

  getMetricOptions(
    includeLTMetrics = false,
    include = DEFAULT_METRICS
  ): SelectItem[] {
    const fields = this.fieldService.getCurrentFields();
    const fieldValues = Object.values(fields);

    // Filter and map in one step with explicit type annotation
    return fieldValues.reduce<SelectItem[]>((acc, field) => {
      if (
        include.includes(field.literalId) &&
        (includeLTMetrics || !field.literalId.endsWith('LT'))
      ) {
        acc.push({ label: field.label, value: field.literalId });
      }
      return acc;
    }, []);
  }

  /**
   * Returns metric options grouped by category (Business, Platform, Incremental)
   * @param includeLTMetrics Whether to include Last Touch metrics
   * @param include List of metrics to include
   * @returns SelectItem[] with grouped structure for PrimeNG dropdowns
   */
  getGroupedMetricOptions(
    includeLTMetrics = false,
    include = DEFAULT_METRICS
  ): SelectItemGroup[] {
    // Get flat list of metrics using existing method
    const metrics = this.getMetricOptions(includeLTMetrics, include);
    return MetricsGroupOptions.getSelectItemGroups(metrics);
  }
}
